import callApi from '@/call-api-axios';

/**
 * 콜봇 월 정산(병원 사용량 목록·병원 월 정산·기본 요율·회사 예외·자동 마감·대사) call-api 호출.
 * 요청 문서: ~/md/front/admin-콜봇-월정산-프론트요청.md "요청 계약"
 * 구현: call-api `feature/billing-settlement`(2621bf7, 배포 전). 경로·메서드는 계약 그대로이고, 응답이 달라진 점은
 * ~/md/front/admin-콜봇-월정산-call-api-구현회신.md "응답·동작 차이"(표 1~18) — 아래 JSDoc 은 실제 응답 모양이다.
 * 판매 요금은 요금표 v70 이다(같은 문서 "2026-10-16 개정" — 기본 요율 본문·예외 칸·`rates`·청구 줄·정산서 줄·
 * `usage.inbound`·`usage.outbound`·`statement.legacy`). 상담사 번호 추가 요금(~/md/front/admin-콜봇-상담사번호-추가요금-프론트요청.md,
 * call-api 1-2·1-3)이 요율 블록 `counselor_number`·정산서 일곱째 줄 `counselor_number`·경고 셋·기본 요율 목록의
 * `fee_start_warning` 을 더했다 — 청구 일곱 줄(월 사용료·120분 초과 통화·발신·웹 텍스트·회선·상담사 번호 추가·일회성).
 * 수납 칸(상세 `payment_*`·확정 `expected_rev`·재수정 503)은 ~/md/front/admin-콜봇-월정산-카드결제-프론트요청.md "요청 계약"과 계약 묶음 ~/md/plan/콜봇-월정산-카드결제-조사/v3/contract/contract-pack.md 9장이다.
 *
 * - `callApi` 인스턴스가 `response.data` 를 언랩하므로 각 메서드는 응답 본문을 바로 resolve 한다.
 *   전역 로딩은 켜지 않는다 — 화면 안 스켈레톤·버튼 로딩으로 대신한다.
 * - 전부 CERTILIFE 전용이다. 회사 경로는 운영 스코프 예외로 타사 회사를 부른다(내선 관리와 같다).
 * - 응답은 snake_case, `_at` 은 unix ms, 금액은 원(확정값은 정수, 단가·중간값은 소수).
 *   `month` 는 'YYYY-MM'(KST 월). 형식이 틀리면 400 VALIDATION_ERROR.
 * - 화면은 이 객체를 직접 쓰지 않고 service/settlementMock.js 의 getSettlementApi() 로 받는다
 *   (`?settlementMock=1` 이면 같은 모양의 목). 메서드 이름·인자는 목과 같다.
 * - 에러는 봉투 `{ error: { code, message, details? } }` 그대로 reject 된다 —
 *   views/Callbot/service/callApiError.js 로 읽는다.
 */

// ---------------------------------------------------------------------------
// 응답 형태 (JSDoc) — call-api `src/settlement/settlement.types.ts` 에서 화면이 읽는 것만 옮겼다.
// `?` 는 화면이 없어도 깨지지 않게 쓰는 칸이다(덜 보이고 '—'·"확인 중").
// ---------------------------------------------------------------------------

/** @typedef {'pbx'|'vox_direct'|'mixed'|'none'} ConnectMode 그 달 실제 통화(pbxCalls `connectVia`) 기준 */
/** @typedef {{ mode: ConnectMode, pbx: number, vox_direct: number }} ConnectModeView 경로별 상담사 응답 수(합 = answered) — 목록·상세 같은 모양 */
/** @typedef {'pbx'|'vox_direct'} CounselorRoute */
/** @typedef {'extension'|'landline_070'|'mobile'|'uan'|'other'} NumberType 요율 버킷. other = 미분류 */
/** @typedef {'default'|'override'} RateSource */
/** @typedef {'estimating'|'closing'|'review'|'confirmed'|'failed'} StatementStatus estimating = 정산 문서 없음 */

/**
 * 1. 병원 사용량 목록 행. 미집계 값은 `"…"` 일 수 있다(화면은 '—').
 * @typedef {Object} UsageMonthlyItem
 * @property {string} company_id
 * @property {string|null} company_name
 * @property {boolean} [company_deleted] 삭제된 회사 — 그 달 사용량이 있어 목록·자동 마감에 나온다(회신 표 6). 정산 라우트·통화 단건·리포트도
 *   삭제된 회사를 받는다(회신 "2026-10-19 개정")
 * @property {boolean} internal 그 달 유효 요율의 internal — 구분 배지·그룹
 * @property {ConnectModeView} connect_mode
 * @property {number} calls 웹 제외(수신 + 발신, AI 가 받기 전 끊긴 통화 포함)
 * @property {number} web 웹 테스트 통화 수 — vox 스냅샷에서 온다. 마감·[vox 건수 확인] 전 달은 대개 0(회신 표 7)
 * @property {number} outbound
 * @property {number|string} canceled_before_ai
 * @property {number} not_final 미종료 통화 수
 * @property {number} ai_seconds 웹 제외 AI 구간 초 합
 * @property {number} handoffs 상담사 연결 요청 통화 수
 * @property {number} answered 상담사가 받은 통화 수
 * @property {number|string} counselor_talk_seconds 상담사가 받은 leg 의 통화 초 합(PBX + vox 직접)
 */

/**
 * GET /api/v1/callbot/usage/monthly — `hospital_totals` 는 internal:false 행의 합(목록 타일)
 * @typedef {Object} UsageMonthlyResponse
 * @property {string} month
 * @property {number} generated_at
 * @property {Omit<UsageMonthlyItem, 'company_id'|'company_name'|'internal'|'connect_mode'> & { companies: number, connect_mode: { pbx: number, vox_direct: number } }} hospital_totals
 * @property {UsageMonthlyItem[]} items
 */

/**
 * 원가 참고값(3-1 `cost_rates`) — 요율과 같은 모양에서 판매가를 뺀 것. 단위·원가를 모르면 둘 다 null + `pending`.
 * @typedef {Object} CostRates
 * @property {string} as_of
 * @property {{ unit_sec: number, cost_per_unit: number }} ai
 * @property {number} web_text_per_message
 * @property {{ pbx: Record<'extension'|'landline_070'|'mobile'|'uan', { unit_sec: number|null, cost: number|null, pending?: string }> & { stt: { unit_sec: number, cost: number, basis: string, cost_source?: string, cost_tiers?: Array<{ upto_hours: number|null, per_hour: number }>, min_sec_per_session?: number } }, vox_direct: { transfer: { unit_sec: number, cost: number }, telecom: Record<'landline_070'|'mobile'|'uan', { unit_sec: number|null, cost: number|null, pending?: string }> } }} counselor
 * @property {{ '070': number, uan: number }} line 월 원가
 * @property {Object} extras 부가 항목 원가(레터링·문자·개인정보 보호, `outbound_base_per_call` — 발신 건당 칸의 원가 참고)
 * @property {{ monthly: number, scope: string }} subscription
 * @property {{ rate: number, rounding: 'floor' }} vat
 */

/**
 * 요율 칸 모양(3-1 본문, 요금표 v70 — 개정 1). 서버가 채우는 칸(`unit_sec`·`cost`·`cost_per_unit`·`cost_per_message`, `ai`·
 * `counselor` 블록 전체)은 생략할 수 있다(보내도 cost_rates 로 다시 채운다). 위반은 400 VALIDATION_ERROR `details.errors[]`
 * ({ path, reason } — not_integer·null_not_allowed·unknown_key·required·…). 예전 본문의 칸(`ai.price_per_unit`·
 * `counselor.*.price`·`extras.outbound_base`·`extras.voicemail_or_failed`)은 unknown_key 400 이다.
 * @typedef {Object} RatePlanBody
 * @property {boolean} internal 기본 요율에서는 false 고정
 * @property {'excluded'} vat_mode
 * @property {{ monthly_vat_included: number, included_minutes: number }} base 월 사용료(VAT 포함 원 정수)·포함 통화(분 정수). null 불가
 * @property {{ unit_sec?: number, overage_price_per_minute: number }} inbound 120분 초과 1분당(2026-10-16 — 예전
 *   `overage_price_per_unit` 은 unknown_key 400). 10초 단위로 세고 금액 = ⌊초과 단위 × 1분당 ÷ 6⌋. null 불가
 * @property {{ unit_sec?: number, per_call: number, price_per_unit: number }} outbound 발신 건당·받은 통화 10초당. null 불가
 * @property {{ pass_through: boolean, cost_per_message?: number }} web_text
 * @property {{ '070': { monthly: number|null, prorate?: boolean, cost?: number }, uan: { monthly: number|null, prorate?: boolean, cost?: number } }} line
 *   070 은 추가 회선 1개의 월 금액(1회선은 월 사용료에 포함). null = 미정(LINE_RATE_UNSET). prorate 를 빼면 직전 버전 값을 잇는다
 * @property {{ unit_sec: number, cost_per_unit: number }} [ai] 원가 참고값(판매가 칸 아님)
 * @property {{ pbx: Record<'extension'|'landline_070'|'mobile'|'uan', { unit_sec: number|null, cost: number|null }> & { stt: { unit_sec: number, cost: number, basis: 'talk_seconds_x2_monthly_prorata' } }, vox_direct: { transfer: { unit_sec: number, cost: number }, telecom: Record<'landline_070'|'mobile'|'uan', { unit_sec: number|null, cost: number|null }> } }} [counselor]
 *   원가 칸만(판매가 없음 — 상담사 연결은 월 사용료에 포함)
 * @property {Object} [extras] 부가 항목(판매가 전부 null — 계산·경고 대상 아님). 빼면 직전 버전 값을 잇는다
 * @property {{ included: number, monthly: number }|null} [counselor_number] 상담사 번호 추가 요금 블록(call-api 1-2).
 *   included = 기본 포함 번호 수, monthly = 추가 번호 1개당 월 공급가(원, VAT 별도) — 둘 다 0 이상 정수, 키는 이 둘뿐(`prorate` 도
 *   unknown_key 400). null 이면 이 버전부터 요금 없음(monthly 0 도 요금 없음). 빼면 고치는 버전의 블록(새 버전이면 가장 가까운
 *   이전 버전의 블록)을 물려받는다. 목록 응답에는 저장된 그대로 실리고 키가 없거나 null 이면 요금 없음이다. 화면은 늘 보낸다.
 *   400 경로: counselor_number(invalid_type) · counselor_number.<칸>(unknown_key·required·null_not_allowed·invalid_type·negative·
 *   not_integer) — 끄면서 두 칸을 null 로 보내면 400 이다. `changed_cells` 에 counselor_number.included·.monthly 가 생긴다
 * @property {'floor_per_line'} rounding
 * @property {string} [memo]
 */

/** @typedef {{ value: number|null, source: RateSource, default_value?: number|boolean|null }} RatedValue 덮을 수 있는 칸(개정 3) — default_value 는 예외일 때만 */

/**
 * 2. 상세의 유효 요율(개정 3) — 3-1 칸 모양에서 덮을 수 있는 칸이 모두 `{ value, source, default_value? }`(RatedValue).
 * 확정·검토 필요 달은 정산 스냅샷 값이다. 요금표 v70 이전에 만든 정산서(`statement.legacy`)의 스냅샷은 예전 모양
 * (`ai.price_per_unit`·`counselor.*.price` 등, `base` 없음) 그대로 온다.
 * @typedef {Object} Rates
 * @property {string} default_version
 * @property {string|null} override_version
 * @property {{ value: boolean, source: RateSource }} internal
 * @property {{ value: 'excluded', source: RateSource }} vat_mode
 * @property {{ monthly_vat_included: RatedValue, included_minutes: RatedValue }} base
 * @property {{ unit_sec: number, overage_price_per_minute: RatedValue }} inbound 1분당 판매가
 * @property {{ unit_sec: number, per_call: RatedValue, price_per_unit: RatedValue }} outbound
 * @property {{ pass_through: RatedValue, cost_per_message: number }} web_text
 * @property {{ '070': { monthly: RatedValue, prorate: boolean, cost: number }, uan: { monthly: RatedValue, prorate: boolean, cost: number } }} line
 * @property {{ unit_sec: number, cost_per_unit: number }} ai 원가 참고값
 * @property {Object} counselor 원가 칸만(`{ unit_sec, cost }`, source 없음)
 * @property {{ included: RatedValue, monthly: RatedValue }|null} [counselor_number] 상담사 번호 추가 요금(call-api 1-2). null 이면
 *   그 달 기본 요율에 블록이 없어 요금 없음(예외 칸도 무시된다). 1-2 전에 찍은 v70 스냅샷도 null 로 읽힌다
 * @property {Object} [extras]
 */

/**
 * 경고(7. 경고 코드 + WEB_USAGE_UNVERIFIED — 회신 표 1). 차단 여부는 언제나 `blocking` 이 정한다.
 * @typedef {Object} SettlementWarning
 * @property {string} code
 * @property {number|null} [count] VOX_DIRECT_COLD_UNMEASURED·WEB_USAGE_UNVERIFIED·DEFAULT_PLAN_MISSING·RECONCILIATION_UNAVAILABLE 는 null
 * @property {any} [details]
 *   ROUTE_RATE_MISSING [{ route, number_type(|'stt'), count }] · TELECOM_RATE_UNSET [{ number_type, count }] ·
 *   LINE_RATE_UNSET [{ line_type, number_last4, days }] · NOT_FINAL_CALLS { calls, pbx_legs } ·
 *   COUNT_MISMATCH { db_total, vox_live_total, db_non_web_total, vox_live_non_web_total, fetched_at } ·
 *   WEB_USAGE_UNVERIFIED { reason: 'not_read'|'incomplete'|'before_month_end', source, fetched_at } ·
 *   VOX_DIRECT_COLD_UNMEASURED { reason, vox_direct_legs } · UNIT_RULE_MISMATCH { target, matched, other } ·
 *   WEB_ONLY_USAGE { web } · RECONCILIATION_UNAVAILABLE { status, http_status, code } ·
 *   WEB_STORED_MISMATCH(비차단, 끝난 달만, 2026-11-02) { checked_at, vox_calls, stored_calls, missing_count, missing_ids,
 *   extra_count, extra_ids, cost_changed_count } — 끝까지 읽은 vox 웹 조회 ↔ 저장한 웹 통화(webTestCalls) 차이,
 *   count = missing_count + extra_count, id 목록은 앞 100개까지. 청구는 vox 요약 기준이라 금액 영향 없음(웹훅 진단용) ·
 *   DEFAULT_PLAN_MISSING { month, legacy_version } — legacy_version: 그 달 기본 버전이 v70 이전 모양이면 그 버전(아니면 null) ·
 *   INCLUDED_LINE_MISSING(비차단, 개정 7) { vox_lines, real_calls } — 그 달 vox 070 회선이 없어 월 사용료 전액(전화는 있음) ·
 *   NO_PHONE_USAGE(차단, 2026-10-16) { vox_lines, real_calls: 0 } — vox 070 회선도 웹 제외 통화도 없는 달(개통 전 등).
 *   둘은 함께 나오지 않는다. 순서는 NO_PHONE_USAGE 가 차단 무리 끝(WEB_VOICE_COST_MISMATCH 뒤) ·
 *   STATEMENT_DRIFT { changed: [{ path, snapshot, live }] } ·
 *   UNIT_RULE_UNCOSTED(비차단) { count, call_ids } — 원가 없는 통화(call_ids 최대 20) ·
 *   WEB_VOICE_COST_MISMATCH(차단) { voice_calls, voice_units_10s, expected_vox_cost, vox_cost, diff } — 웹 음성 단위 × 원가 단가 ≠ vox 원가.
 *   이 둘은 call-api 가 같은 릴리즈에 더했고 끝난 달에만 낸다 — 옛 서버에는 없다.
 *   ROUTE_RATE_MISSING·TELECOM_RATE_UNSET 은 요금표 v70 부터 새로 계산한 달에는 나오지 않는다(v70 이전 스냅샷에만 남는다) ·
 *   상담사 번호 추가 요금(call-api 1-2 — 기본 요율이 없거나 줄이 no_rate 면 셋 다 내지 않는다):
 *   COUNSELOR_COUNT_GAP(차단, LINE_RATE_UNSET 다음) count = 빈 날 수, [{ day, count, before: { day, count }|null, after: { day, count }|null }]
 *   — count 는 그날 쓴 값(번호를 추가할 때의 기록, 없으면 0), 앞뒤는 매시 기록이 있는 가장 가까운 날(참고값). 통화 0건 달에는 없다 ·
 *   COUNSELOR_FEE_DUPLICATE_SUSPECT(차단, GAP 다음) count = 걸린 항목 수, { amount, items: [{ item_id, name, amount }] } — 줄 금액 > 0
 *   이고 일회성 항목 이름(공백 뺀)에 '상담사번호'. 검토 정산서는 저장·일회성 쓰기·다시 계산 때 다시 판정한다 ·
 *   COUNSELOR_FEE_NO_CALLS(비차단, INCLUDED_LINE_MISSING 다음) count null, { extra_number_days, amount } — amount 는 계산했을 금액
 * @property {boolean} blocking
 */

/** @typedef {{ amount: number, estimate: number, overridden: boolean, memo: string|null }} ChargeLineHead 청구 줄 머리 — 라이브 계산은 memo null */

/**
 * 상담사 번호 추가 줄(call-api 1-2 CounselorNumberChargeLine). 금액 = ⌊monthly × extra_number_days ÷ days_in_month⌋ — 그 달 합에
 * 한 번만 버리고 화면은 다시 계산하지 않는다. 구간(segments)에는 금액이 없다. 날은 KST 'YYYY-MM-DD'.
 *  - applies 가 없으면 줄 근거가 없는 빈 줄 모양이다(1-2 전에 만든 검토 필요·확정 정산서, 기본 요율 없이 사람이 금액을 넣은
 *    정산서) — amount·estimate·overridden·memo 만 정산서 줄 값(보통 0, 사람이 넣었으면 그 값)이고 나머지는 빈 값(reason null 은
 *    요금 적용이라는 뜻이 아니다)
 *  - reason 'no_rate'(블록 없음·monthly 0·예외 면제 0 — 근거 없음, window null) · 'no_calls'(웹이 아닌 통화 0건 — 0원, 근거는
 *    남는다) · null(요금 적용). no_rate 가 no_calls 보다 앞선다
 *  - 내부·테스트는 amount·estimate 0, 나머지는 그대로. 진행 중인 달은 오늘까지 센다(뒤의 날은 빈 날이 아니다)
 *  - warning 은 계산할 때 낸 첫 경고(GAP → DUPLICATE → NO_CALLS) — 검토 쓰기가 다시 판정한 중복 의심과 어긋날 수 있어 화면은
 *    `warnings[]` 를 따른다
 * @typedef {ChargeLineHead & { applies?: boolean, reason: 'no_calls'|'no_rate'|null, included: number|null, monthly: number|null,
 *   days_in_month: number, window: { from: string, to: string, days: number }|null, extra_number_days: number, peak_count: number,
 *   peak_extra: number, peak_day: string|null, peak_numbers: Array<{ name: string, number_tail: string }>, peak_numbers_truncated: boolean,
 *   segments: Array<{ from: string, to: string, count: number, extra: number, days: number, extra_number_days: number }>,
 *   gap_days: string[], rate_source: RateSource, warning: string|null }} CounselorNumberChargeLine
 *   peak_*: 청구 창 안에서 하루 개수가 가장 많았던 첫 날과 그날 기록의 번호(이름, 끝 4자리 — 처음 50행, 오래된 행부터),
 *   peak_numbers_truncated 는 기록이 처음 50행만 남겼을 때. window 는 개통 달이면 포함 070 회선이 켜진 날부터 그 달 끝
 */

/**
 * 청구 일곱 줄(요금표 v70 개정 4 + 상담사 번호 추가 — call-api 1-2). 원 미만은 줄마다 한 번 버린다. 예전 `ai`·`counselor` 줄은
 * 없어졌다. 상담사 번호 추가 줄이 없는 응답(1-2 전 call-api)은 0원으로 읽는다.
 * @typedef {Object} ChargeLines
 * @property {ChargeLineHead & { monthly_vat_included: number, monthly_supply: number, opening_month: boolean, days: number, days_in_month: number, included_line: { number_last4: string, days: number, active_from: number|null }|null, rate_source: RateSource, warning: 'NO_PHONE_USAGE'|'INCLUDED_LINE_MISSING'|null }} base
 *   월 사용료 — 공급가액 ⌊VAT 포함 × 10 ÷ 11⌋, 개통 달만 포함 회선 일수로 일할
 * @property {ChargeLineHead & { phone_units_10s: number, web_voice_units_10s: number, used_units_10s: number, included_minutes: number, included_units_10s: number, overage_units_10s: number, unit_sec: number, price_per_minute: number, overage_amount: number, price_per_minute_rate_source: RateSource, included_minutes_rate_source: RateSource }} inbound
 *   120분 초과 통화 — 금액 = ⌊초과 단위 × price_per_minute ÷ 6⌋(정확히, 33.33 같은 반올림 없이). overage_amount 는 원 미만을
 *   버리기 전 금액을 1전 아래에서 버린 값(소수 둘째 자리)
 * @property {ChargeLineHead & { dialed: number, per_call: number, calls_amount: number, answered: number, units_10s: number, unit_sec: number, unit_price: number, talk_amount: number, per_call_rate_source: RateSource, unit_price_rate_source: RateSource }} outbound
 * @property {ChargeLineHead & { calls: number, vox_cost: number, pass_through: boolean, rate_source: RateSource }} web_text
 * @property {ChargeLineHead & { items: Array<{ number_last4: string, type: string|null, rate_key: '070'|'uan'|null, days: number, monthly: number|null, prorate: boolean, amount: number|null, rate_source: RateSource|null, warning: string|null, included: boolean }>, rate_source: RateSource }} line
 *   포함 회선 항목은 included: true · monthly 0 · amount 0 · rate_source null
 * @property {CounselorNumberChargeLine} [counselor_number] 회선 다음 — 상담사 번호 추가(기본 포함 수를 넘는 번호, 일할, VAT 별도,
 *   원가 없음 — 금액 전부 마진)
 * @property {{ items: Array<{ item_id: string, name: string, amount: number, memo: string }>, amount: number }} one_off
 */

/**
 * 4-3 정산 객체(call-api StatementView). 상세 `statement` 는 이것이고(스냅샷·경고 없음), 정산 문서가 없으면 null(추정 중).
 * 처리 라우트(저장·다시 계산·확정·재수정) 응답은 여기에 스냅샷(`*_snapshot`)과 `warnings` 를 더한 것이다.
 * @typedef {Object} Statement
 * @property {Exclude<StatementStatus, 'estimating'>} status
 * @property {{ base?: StatementLine, inbound?: StatementLine, outbound?: StatementLine, web_text?: StatementLine, line?: StatementLine, counselor_number?: StatementLine, one_off?: { items: OneOffItem[], amount: number } }} [lines]
 *   정산 줄 여섯(요금표 v70 다섯 줄 — 개정 6 — 과 상담사 번호 추가) + 일회성. 상담사 번호 추가 줄이 없게 저장된 정산서는
 *   { 0, 0, false, '' } 로 읽히고(call-api 1-2) total·vat_amount 는 그대로다. 키가 없는 응답(1-2 전)은 화면이 0 으로 읽는다
 *   처리 라우트(B11~B14) 응답의 charge_snapshot·usage_snapshot 은 저장된 그대로라 1-2 전 정산서에는 이 줄이 없다 — 상세(B2)의
 *   charge.lines 를 읽는다
 * @property {number} total 공급가액(부가세 별도)
 * @property {number} [vat_amount] floor(total × 0.1)
 * @property {'excluded'} [vat_mode]
 * @property {boolean} [internal]
 * @property {boolean} legacy 개정 8 — 요금표 v70 이전 줄(`ai`·`counselor`)로 만든 정산서. 화면은 이 값 하나로만 가른다. 새 줄은 0, total·vat_amount 는 그때
 *   저장한 값이고 상세의 charge·margin 은 null 이다. 저장·확정·일회성 항목 쓰기는 409 STATEMENT_LEGACY_LINES — [다시 계산]
 *   (확정이면 [재수정] 먼저)으로 새 줄이 된다
 * @property {number|null} confirmed_at
 * @property {string|null} confirmed_by 'system' | 사용자 id
 * @property {string|null} confirmed_by_name system 이면 null
 * @property {string[]} [acknowledged_warnings]
 * @property {boolean} reopened
 * @property {{ total: number, vat_amount?: number, confirmed_at: number, confirmed_by: string, confirmed_by_name?: string|null }|null} last_confirmed 재수정 중 직전 확정
 * @property {boolean} [human_touched]
 * @property {number} [rev]
 * @property {{ code: string, message: string, at: number }|null} [close_error] 마감 실패 사유. vox 일시 실패는 VOX_REFRESH_FAILED —
 *   직전이 검토 필요였으면 상태는 review 그대로 이것만 남는다(회신 표 2)
 * @property {string|null} [run_id]
 * @property {string} [memo]
 * @property {number|null} [snapshot_at]
 * @property {Array<{ at: number, by: string, by_name?: string|null, action: 'auto_confirm'|'auto_review'|'auto_recalculate'|'recalculate'|'save'|'one_off'|'confirm'|'reopen', total_before: number|null, total_after: number|null, memo?: string }>} [revisions]
 */

/** @typedef {{ amount: number|null, estimate: number|null, overridden: boolean, memo: string }} StatementLine 저장할 때 amount 는 0 ~ 10,000,000,000 정수, null 이면 계산값을 따른다(회신 표 13) */

/**
 * 건수 대사 값(call-api ReconciliationView). 판정은 웹을 뺀 건수끼리(허용 오차 0, 월말 뒤에 읽은 vox 값만 — 회신 표 3).
 * @typedef {Object} Reconciliation
 * @property {number} db_total DB 웹 제외 + 웹(vox 웹 요약, 없으면 미러 — 웹콜이 없다)
 * @property {number} db_non_web_total
 * @property {number|null} vox_live_total 웹 포함 — vox 웹 요약을 끝까지 읽었을 때만 값이 있다
 * @property {number|null} vox_live_non_web_total
 * @property {number|null} vox_live_fetched_at 웹 제외 건수를 읽은 시각
 * @property {'vox'|'stored'|'mirror'} web_source 상세 `usage.web_source` 와 같은 뜻
 * @property {null} invoice_call_usage_quantity 인보이스 대조는 이번에 없다(회신 표 15)
 */

/**
 * 2. GET /api/v1/companies/:companyId/callbot/billing/settlement?month= — 병원 월 정산 상세.
 * 기본 요율 버전이 없는 달(그 달 기본 버전이 v70 이전 모양인 달 포함)은 rates·charge·margin 이 null(DEFAULT_PLAN_MISSING).
 * v70 이전에 만든 정산서(`statement.legacy`)는 charge·margin 이 null 이고 rates·usage 가 예전 모양이다(개정 8).
 * @typedef {Object} SettlementDetail
 * @property {string} company_id
 * @property {string|null} company_name
 * @property {string} month
 * @property {'hospital'|'internal'} category
 * @property {ConnectModeView} connect_mode 목록과 같은 객체(회신 표 4 — 계약의 문자열이 아니다)
 * @property {Rates|null} rates
 * @property {{ calls: { total: number, ai_answered: number, canceled_before_ai: number, web: number, outbound: number, not_final: number }, ai: { seconds: number, units_10s: number }, web_text: { calls: number, vox_cost: number }, web_voice?: { calls: number, seconds: number, units_10s: number, vox_cost: number }, inbound?: { calls: number, ai_seconds: number, counselor_talk_seconds: number, units_10s: number, unmatched_legs: number }, outbound?: { calls: number, dialed: number, answered: number, ai_seconds: number, counselor_talk_seconds: number, units_10s: number }, web_source?: 'vox'|'stored'|'mirror', web_fetched_at?: number|null, connect_mode?: ConnectModeView, counselor: { requested: number, answered: number, talk_seconds: number, segment_seconds: number, legs: Array<{ route: CounselorRoute, number_type: NumberType, answered: number, talk_seconds: number, units: number|null, transfer_units?: number, telecom_units?: number|null }> }, lines: Array<{ number_last4: string, type: string|null, rate_key?: '070'|'uan'|null, days: number, monthly_fee: number|null, amount: number|null, source: 'estimate' }> }} usage
 *   검토 필요·확정 달은 마감 시점 스냅샷이다(회신 B12). `ai_answered` 는 발신을 뺀 값(B14).
 *   `web_source` — 'vox': 끝까지 읽은 vox 웹 요약. 'stored'(2026-11-02): vox 요약이 없거나 덜 읽혀, 저장한 웹 통화
 *   (웹훅 + 이전 vox 조회)와 미러 웹 행을 합친 값(vox 확인 전). 'mirror': 저장한 웹 통화도 없다(9/7 이전 달 등) — vox 웹
 *   요약을 읽지 않아 웹 테스트가 비었거나 덜 찼을 수 있다(회신 표 5). 끝난 달의 WEB_USAGE_UNVERIFIED 규칙은 'stored' 도 같다.
 *   `inbound`·`outbound`(개정 5)는 청구 사용량 — 통화마다 AI + 상담사 통화를 더해 10초 올린 단위다. `inbound.units_10s` 는
 *   전화만(웹 음성은 `web_voice.units_10s`). `ai`(통화마다 AI 만 올린 단위)는 그대로이고 10초 규칙(원가 대사)의 기준이다.
 *   v70 이전 정산서의 스냅샷에는 두 블록이 없다.
 *   `counselor_numbers`(call-api 1-2, 화면은 읽지 않는다 — 근거는 charge.lines.counselor_number): { days: [{ day, count, filled }],
 *   gap_days(그 달 전체), covered_days, peak_day, peak_numbers, peak_numbers_truncated } — 1-2 전에 저장한 스냅샷에는 없다
 * @property {{ vox_call: number, split: { voice_ai: number, web_text: number, other: number, vox_direct_transfer?: number, vox_direct_telecom?: number }, unit_rule: { target: number, matched: number|null, other: number|null, mismatched: number|null, uncosted?: number, uncosted_call_ids?: string[], mismatches: Array<{ vox_call_id: string, ai_seconds: number, expected_cost: number, vox_cost: number, diff: number }> }, line: number, total: number, pbx_trunk_estimate: number, stt_estimate: number }} cost
 *   `split.voice_ai` 는 전화 AI 몫 + 웹 음성 원가다. `unit_rule` 의 대상(target) = 일치 + 기타 + 불일치 + 원가 없음(uncosted) —
 *   AI 가 받은 웹 제외 통화(수신 + 발신)로, `usage.ai` 의 시간·단위와 같은 모집단이다. `uncosted_call_ids` 는 최대 20건(옛 서버에는 없다)
 * @property {{ lines: ChargeLines, total: number, vat_amount: number, source: 'confirmed'|'review'|'estimate', partial: boolean }|null} charge
 *   total = 일곱 줄의 합(공급가액, 상담사 번호 추가 포함). partial 은 LINE_RATE_UNSET 으로 뺀 회선이 있을 때만 true.
 *   기본 요율이 없는 달은 null — 다만 사람이 금액을 넣었으면 그 금액만 담아 온다(rates·margin 은 null, 회신 표 16).
 *   v70 이전 정산서(`statement.legacy`)도 null. 내부·테스트는 줄 금액·계산값이 0 이다
 * @property {{ amount: number, basis: 'vox_cost', amount_incl_pbx_trunk: number, vat_mode: 'excluded' }|null} margin 내부·테스트·기본 요율 없는 달·v70 이전 정산서는 null
 * @property {Reconciliation} reconciliation
 * @property {Statement|null} statement
 * @property {SettlementWarning[]} warnings
 * @property {{ enabled: boolean, close_day: number, close_hour?: number, scheduled_at: number|null, next_retry_at: number|null }} [auto_close]
 *   scheduled_at: 2026-09 이전 달은 null(자동 마감 대상 아님). next_retry_at: 정산 상태와 상관없이 그 달을 다시 집는 다음 자동
 *   실행 시각 — 꺼졌거나 남은 자동 실행이 없으면 null(회신 표 9). 없으면 켜짐·3일·06:00 KST 로 본다
 * @property {PaymentScope} [payment_scope] 수납 갈래(계약 묶음 9.1). 칸이 없으면(옛 call-api) inactive 로 본다
 * @property {SettlementPayment|null} [payment] 수납 상태 한 줄 요약. 채널 모드가 off 이거나 그 병원·달에 결제 요청 문서가 없으면 null
 *   (칸이 없으면 null 로 본다)
 * @property {{ code: 'PAYMENT_STATE_UNREADABLE'|'PW_LEDGER_UNREADABLE', message: string }|null} [payment_error] 결제 쪽 읽기 실패 —
 *   PAYMENT_STATE_UNREADABLE 은 채널·요청 문서(이때 payment_scope 도 null), PW_LEDGER_UNREADABLE 은 PW 거울만(payment_scope 는 그대로).
 *   있으면 payment 는 null. 결제 쪽 읽기가 실패해도 상세는 5xx 가 되지 않는다
 * @property {PaymentConfirmPreview|null} [payment_confirm_preview] 확정 창의 X → Y → Z
 */

/**
 * 정산 상세의 수납 갈래(계약 묶음 9.1 `payment_scope`). 먼저 맞는 값: in = 그 병원·달의 결제 요청 문서가 있거나, 채널 모드가 off 가
 * 아니고 수납 시작 월이 있고 그 달이 그 이후 · before_start = 수납 시작 월 이전 · inactive = 그 밖 · null = 판단 읽기(채널·요청 문서)
 * 실패 — 확정은 in 으로 본다(9.2). service/settlementPayment.js paymentScopeOf 가 읽는다.
 * @typedef {'in'|'before_start'|'inactive'|null} PaymentScope
 */

/**
 * 정산 상세 `payment` — 수납 상태 한 줄 요약(계약 묶음 9.1, AC 결제 요청 계약 3. 이름은 계약 4 PaymentView 와 같다). 읽기 전용이고
 * 숫자는 서버 값만 쓴다(화면이 더하거나 빼지 않는다).
 * @typedef {Object} SettlementPayment
 * @property {number} seq 지금 서명 버전
 * @property {number|null} applied_seq PW 가 적용한 seq(PW 장부가 없으면 null). seq 와 다르면 장부 상태 자리는 '반영 중'
 * @property {number} statement_rev 지금 서명 버전의 정산서 rev. 상세 `statement.rev` 와 다르면(재확정 직후) '반영 중'
 * @property {'ready'|'held'|'suspended'} state
 * @property {string} reason 그 버전이 생긴 사유(계약 묶음 3.4). suspended 면 멈춘 사유(REOPENED·NOT_CONFIRMED·EXCLUDED)
 * @property {string[]} holds held 일 때 지금 막는 보류 전부, 그 밖에는 []
 * @property {'subscription'|'statement'|null} base_mode subscription = 기본료 제외(A), statement = 정산서 전부(B).
 *   서명된 `target.baseMode`, 없으면 요청 문서의 `pinnedBaseMode`. 둘 다 없으면(목표가 없고 아직 고정한 방식도 없는 버전)
 *   null 이고(계약 묶음 9.1), 한 줄에서 그 칸은 빠진다(paymentStatusLine)
 * @property {{ method: 'bank_transfer'|'card', due_on: string|null, due_at: number|null }} collection
 *   due_on 은 계좌이체 입금 기한 'YYYY-MM-DD'(화면은 이 문자열로 '기한 M월 D일'), due_at 은 그다음 날 00:00 KST(unix ms).
 *   계좌이체 ready 버전에만 있다 — 카드 달과 보류·멈춤 버전은 둘 다 null(계약 묶음 3.1)
 * @property {{ supply: number|null, vat: number|null, gross: number|null }} amounts 서명된 목표. 목표가 없는 버전(INVALID_STATEMENT·
 *   HOLD_NO_PROFILE·INTERNAL_FLAG_CHANGED, 목표가 null 인 모르는 보류 코드, suspended)은 셋 다 null, 청구 면제(give_up)는 supply·vat 만 null
 * @property {{ status: string, status_reason: string|null, overdue: boolean, net: number, card_net: number, bank_net: number }|null} pw
 *   PW 장부 요약. 장부가 아직 없으면 null. status 는 결제 계획 7.3 의 14개(모르는 값은 그대로 온다), overdue 는 기한 지남 표식
 * @property {number|null} remaining amounts.gross − pw.net(call-api 계산, 음수면 돌려줄 돈). pw 가 null 이거나 목표가 없으면 null
 */

/**
 * 정산 상세 `payment_confirm_preview` — 확정 창의 "이미 받은 X → 새 대상 Y → 차액 Z"(계약 묶음 9.1. AC 결제 요청 계약 3 의 칸에
 * supply·vat 를 더했다). 정산서가 검토 필요(재수정 포함)이고 payment_scope 가 in 일 때만 채운다. 목표를 정할 수 없으면(레거시 줄,
 * 줄 합·부가세 불일치, 결제 설정 없음, 결제 제외, 확정 뒤 내부 표시) 또는 결제 쪽 읽기가 실패하면 null. 내부 회사면 gross·supply·vat 0.
 * @typedef {Object} PaymentConfirmPreview
 * @property {number} net X — 지금 받은 순액(요청 문서나 PW 장부가 없으면 0)
 * @property {number} gross Y — 지금 저장된 정산서를 확정하면 계획기가 처음 계산할 목표 합계(상한 선택·청구 취소·면제 전)
 * @property {number} supply Y 의 공급가(화면은 보이지 않는다)
 * @property {number} vat Y 의 부가세(화면은 보이지 않는다)
 * @property {number} delta Z = gross − net(+ 더 받을 돈, − 돌려줄 돈)
 * @property {'subscription'|'statement'} base_mode Y 를 계산한 기본료 방식
 */

/**
 * 3-1. 기본 요율 버전(적용 시작 월 내림차순). `legacy: true` 는 요금표 v70 이전 모양으로 저장된 버전 — 저장된 모양
 * 그대로(예전 칸) 오고, 그 버전이 적용되는 달은 금액을 계산하지 않는다(DEFAULT_PLAN_MISSING `legacy_version`). 새 본문으로
 * PUT 하면 풀린다(자동 변환 없음). `changed_cells` 는 정렬된 점 경로다.
 * @typedef {RatePlanBody & { effective_from: string, legacy: boolean, confirmed_statements: number, created_at: number, created_by: string, created_by_name?: string|null, updated_at: number, updated_by: string, updated_by_name?: string|null, revisions: Array<{ at: number, by: string, by_name?: string|null, changed_cells: string[], memo: string }> }} DefaultPlan
 */

/**
 * 3-2. 회사 예외 버전 — 본문은 덮은 칸만 담는다(빠진 칸은 기본을 따른다). 칸이 없으면 "예외 없음" 버전.
 * @typedef {Object} PlanOverride
 * @property {string} company_id
 * @property {string} effective_from
 * @property {boolean} [internal]
 * @property {{ monthly_vat_included?: number, included_minutes?: number }} [base] 0 이상 정수
 * @property {{ overage_price_per_minute: number }} [inbound] 1분당
 * @property {{ per_call?: number, price_per_unit?: number }} [outbound]
 * @property {{ pass_through: boolean }} [web_text]
 * @property {{ '070'?: { monthly: number }, uan?: { monthly: number } }} [line] 070 은 추가 회선 1개의 월 금액
 * @property {{ included?: number, monthly?: number }} [counselor_number] 상담사 번호(call-api 1-2) — 0 이상 정수, 면제는 monthly 0.
 *   그 달 기본 요율에 블록이 없으면 무시된다(예외만으로 요금을 켜지 않는다). null 잎은 null_not_allowed, null·객체 아닌 블록은
 *   invalid_type, 다른 칸은 unknown_key(400)
 * @property {{ price_per_unit: number }} [ai] v70 이전에 저장한 버전에만 남은 칸 — 계산에 쓰지 않는다(다시 저장하면 빠진다)
 * @property {{ pbx?: Object, vox_direct?: Object }} [counselor] 위와 같음(예전 `*.price` 칸). 보내면 unknown_key 400
 * @property {string} memo
 * @property {string[]} cells 덮은 칸 경로
 * @property {number} confirmed_statements
 * @property {number} created_at
 * @property {string} created_by
 * @property {string|null} [created_by_name]
 * @property {number} updated_at
 * @property {string} updated_by
 * @property {string|null} [updated_by_name]
 */

/**
 * 4-1. GET /api/v1/callbot/billing/close-runs?month= — 마감 현황(기본 요율 화면 위 자동 마감 요약)
 * @typedef {Object} CloseRunsResponse
 * @property {string} month
 * @property {{ enabled: boolean, close_day: number, close_hour?: number, scheduled_at: number|null, last_run: { run_id: string, trigger?: 'auto'|'manual', catch_up?: boolean, company_id?: string|null, started_at: number, finished_at: number|null, error?: string|null, targets?: number, confirmed: number, review: number, failed: number, skipped: number }|null }} auto_close
 *   last_run: 자동이든 수동이든 마지막 실행. `company_id` 는 회사 하나만 돈 수동 실행, `error` 는 실행 전체가 중간에 멈춘 사유,
 *   집계는 합류한 인스턴스까지 합친다(회신 표 8). 자동 마감하지 않는 달은 `scheduled_at: null`
 * @property {{ estimating: number, closing: number, review: number, confirmed_system: number, confirmed_human: number, failed: number }} counts
 * @property {Array<{ company_id: string, company_name: string|null, company_deleted?: boolean, status: 'review'|'failed'|'closing', reopened: boolean, blocking_codes: string[], error_code: string|null, error_message?: string|null }>} attention
 *   회사명 오름차순. `closing` 은 lease 가 만료돼 멈춘 마감(error_code CLOSE_INTERRUPTED — 마감 중단)
 */

/**
 * 4-4. 일회성 항목(사용량과 상관없는 청구 — 단말기 등). amount 는 0 이상 정수(공급가액).
 * @typedef {Object} OneOffItem
 * @property {string} item_id
 * @property {string} company_id
 * @property {string} month
 * @property {string} name
 * @property {number} amount
 * @property {string} memo
 * @property {number} created_at
 * @property {string} created_by
 * @property {string|null} [created_by_name]
 * @property {number} updated_at
 * @property {string} updated_by
 * @property {string|null} [updated_by_name]
 */

/**
 * 5. 건수 대사(요청 시에만 vox 호출). 409 ORGANIZATION_NOT_PROVISIONED·ORGANIZATION_KEY_NOT_READY, vox 한도면 429.
 * 상세 `reconciliation`(Reconciliation)에 누락 ID 결과를 더한 것이다. 누락 ID 차집합도 웹 제외다.
 * @typedef {Reconciliation & {
 *   missing: { vox_only: Array<{ vox_call_id: string, created_at: number|null, call_type: string|null }>, db_only: Array<{ vox_call_id: string, created_at: number|null, call_type: string|null }> }|null,
 *   missing_unavailable_reason: 'not_requested'|'too_many_calls'|null,
 *   cached: boolean
 * }} ReconciliationResponse
 *   missing_unavailable_reason: `missing_ids=false` 면 not_requested, 100페이지 또는 25초를 넘기면 too_many_calls(회신 B17)
 */

/**
 * 6. 통화 단건(재사용). `caller_number`(고객 번호)는 화면에 그리지 않는다. 404 = 상담사 연결 leg 없음.
 * CERTILIFE 응답은 `caller_number`·`bot_context` 가 null 이다(회신 C1).
 * @typedef {Object} PbxCall
 * @property {string} vox_call_id
 * @property {string|null} [caller_number] 그리지 않는다(CERTILIFE 응답은 null)
 * @property {string|null} target_counselor_number 끝 4자리만 보인다
 * @property {number|null} received_at
 * @property {number|null} connected_at
 * @property {number|null} ended_at
 * @property {number|null} talk_sec
 * @property {'pbx'|'vox-direct'} connect_via
 * @property {PbxCallAttempt[]|null} [attempts] 상담사 연결 시도 — 시각순(서버가 정렬한다). null = 모름(이 환경에 시도 기록이
 *   아직 없거나 수집 시작 전에 들어온 통화), [] = 기록 없음. 키가 없는 옛 응답은 null 로 본다
 */

/**
 * 통화 단건의 시도 한 건(~/md/front/콜봇-연결시도-attempts-call-api구현회신.md "pbx-calls"). 고객 번호는 싣지 않는다.
 * @typedef {Object} PbxCallAttempt
 * @property {number} attempt_no 식별용(순서가 아니다 — 다시 정렬하지 않는다)
 * @property {'route'|'retry'|'pickup'|string} trigger 모르는 값은 원값
 * @property {string} target_number 숫자만 남긴 상담사 번호 — 끝 4자리만 보인다
 * @property {number|null} candidate_index 라우팅 후보 안 순번(0부터). 당겨받기는 null
 * @property {number|null} candidate_count
 * @property {string} outcome answered·no_answer·busy·unavailable·congestion·rejected·caller_hung_up·not_dialed·superseded·failed(계약 밖 값도 원값)
 * @property {string|null} dial_status 진단용 원값
 * @property {number|null} cause Q.850 cause(진단용)
 * @property {string|null} cause_text
 * @property {number|null} sip_code 진단용
 * @property {number} started_at
 * @property {number|null} ring_started_at 벨이 울리지 않았으면 null
 * @property {number|null} ring_seconds 벨 시간(받았으면 받기까지)
 */

/**
 * 7. 정산 시작일(~/md/back/콜봇-월정산-정산시작일-엑셀내보내기-call-api요청.md 요청 계약 3절 — 배포 전, 모양은 계약서
 * contract-stage23.md 1-5~1-7). 'YYYY-MM-DD' 는 KST 날짜, 시각은 ISO 문자열(목은 같은 모양). 모든 새 칸은 선택 칸이다 — 옛 call-api 는
 * 네 경로가 라우트 없음 404(isCallApiUnimplemented)이고, 목록·상세·마감 현황에 새 칸이 없으면 화면은 시작일 미설정으로 본다.
 * @typedef {'in'|'start_month'|'before_start'} SettlementScope
 *   in = 미설정·시작 월 뒤, start_month = 시작 월(1일 시작이면 금액은 in 과 같다), before_start = 정산 대상 아님(청구·마감 없음)
 * @typedef {{ month: string, before_scope: SettlementScope, after_scope: SettlementScope,
 *   statement_status: 'estimating'|'closing'|'review'|'confirmed'|'failed', action: string,
 *   statement?: { status: string, rev: number, total: number|null, human_touched: boolean } }} SettlementStartAffectedMonth
 *   action(미리보기): none·recalculate_needed·discard_required·close_needed·auto_close_pending·blocked_confirmed·blocked_billed·
 *   blocked_close_in_progress. action(PUT): none·recalculate_needed·discarded·discard_conflict·close_needed·auto_close_pending·
 *   confirmed_during_change. statement 은 discard_required 줄에만(PUT 의 acknowledge_discard 에 그 rev 를 그대로 보낸다)
 * @typedef {Object} SettlementStartDoc GET …/settlement-start (문서 없음 = { start_date: null, rev: 0, history: [] })
 * @property {string|null} start_date
 * @property {number} rev PUT expected_rev 로 보낸다
 * @property {string} [updated_at] ISO
 * @property {string|null} [updated_by_name]
 * @property {Array<{ rev: number, at: string, actor_name: string|null, before: string|null, after: string|null,
 *   reason_code: string, memo: string, affected_months: SettlementStartAffectedMonth[], discarded_months: string[] }>} history
 *   최신 먼저 최대 20. reason_code 는 CONTRACT_START·TEST_PERIOD·CORRECTION·OTHER(서버의 보상 줄은 AUDIT_ROLLBACK)
 * @typedef {Object} SettlementStartPreview GET …/settlement-start/preview
 * @property {{ start_date: string|null, rev: number }} current
 * @property {{ start_date: string|null }} next 지우기면 start_date null
 * @property {SettlementStartAffectedMonth[]} affected_months
 * @property {Array<{ code: 'SETTLEMENT_START_CONFIRMED_MONTH'|'SETTLEMENT_START_CLOSE_IN_PROGRESS'|'SETTLEMENT_START_BILLED_MONTH', months: string[] }>} blockers
 *   하나라도 있으면 PUT 이 같은 code 로 409 다
 * @typedef {Object} SettlementStartBody PUT 본문 — 이 다섯 키만(모르는 키 400)
 * @property {string|null} start_date 2026-09-01 ~ (현재 KST 달 + 12개월) 말일, null = 지우기
 * @property {number} expected_rev GET 의 rev(문서 없으면 0). 다르면 409 SETTLEMENT_START_REV_CHANGED
 * @property {'CONTRACT_START'|'TEST_PERIOD'|'CORRECTION'|'OTHER'} reason_code
 * @property {string} memo 1–500자(trim 뒤)
 * @property {Array<{ month: string, rev: number }>} [acknowledge_discard] 미리보기 discard_required 달 전부(달 목록이 다르면 409
 *   SETTLEMENT_START_DISCARD_REQUIRED `details.statements`)
 */

/**
 * 8. 정산 엑셀 내보내기(같은 요청서 9절 — 서버 JSON + 브라우저 xlsx). 응답 `detail` 은 상세(SettlementDetail)에서 화이트리스트 칸만
 * 옮긴 것이다 — contents.cost_margin 이 거짓이면 원가·마진 칸(cost·vox_cost·margin·reconciliation·rates 원가)이 없고, 상담사
 * 이름·번호 끝자리(peak_numbers)는 늘 없다. 오류: 400 VALIDATION_ERROR(그 달 목록에 없는 id 면 details.unknown_company_ids),
 * 401 SUPPORT_SESSION_ENDED(error.reason), 403 PERMISSION_DENIED, 503 AUDIT_UNAVAILABLE·PERM_UNAVAILABLE(code 로 나눈다),
 * 500 SETTLEMENT_EXPORT_FAILED, 시간 초과는 응답 없는 err.code 'ECONNABORTED'.
 * @typedef {Object} SettlementExportBody 키는 정확히 이것만(reasonNote 는 비면 뺀다)
 * @property {string} month
 * @property {string[]} company_ids 1–100, 중복 없음
 * @property {{ statement: boolean, cost_margin: boolean }} contents 하나 이상 true
 * @property {'CUSTOMER_REQUEST'|'REPORT'|'MIGRATION'|'AUDIT'|'OTHER'} reasonCode
 * @property {string} [reasonNote] OTHER 면 필수
 * @typedef {Object} SettlementExportResponse
 * @property {string} export_id
 * @property {string} month
 * @property {string} generated_at ISO
 * @property {{ statement: boolean, cost_margin: boolean }} contents
 * @property {Array<{ company_id: string, company_name: string, company_deleted: boolean, internal: boolean,
 *   settlement_scope: SettlementScope, settlement_start_date: string|null,
 *   statement_status: 'estimating'|'closing'|'review'|'confirmed'|'failed'|'not_billable',
 *   basis: 'confirmed_snapshot'|'review_snapshot'|'live_estimate'|'not_billable',
 *   summary: { supply: number|null, vat: number|null, total: number|null, vox_cost?: number|null, pre_start_cost?: number|null,
 *     margin?: number|null, margin_revenue?: number|null },
 *   detail: Object }>} companies 요청 company_ids 순서 그대로. summary 의 원가·마진 넷은 cost_margin 일 때만
 */

/**
 * 내보내기만 요청별 시간 제한을 늘린다 — call-api-axios 기본 30초로는 100곳 상세 계산이 모자랄 수 있다
 * [4단계: call-api 회신 확인 사항 6 의 측정값으로 다시 정한다]
 */
export const SETTLEMENT_EXPORT_TIMEOUT_MS = 180000;

// ---------------------------------------------------------------------------
// 경로 헬퍼
// ---------------------------------------------------------------------------

const BILLING_BASE = '/api/v1/callbot/billing';

const companyCallbotBase = (companyId) =>
  `/api/v1/companies/${encodeURIComponent(companyId)}/callbot`;

const companyBillingBase = (companyId) => `${companyCallbotBase(companyId)}/billing`;

const statementBase = (companyId, month) =>
  `${companyBillingBase(companyId)}/statements/${encodeURIComponent(month)}`;

export const settlementApi = {
  // -------------------------------------------------------------------------
  // 1·2. 병원 사용량 목록 / 병원 월 정산 상세 (DB 집계만 — vox 를 부르지 않는다)
  // -------------------------------------------------------------------------

  /**
   * GET /api/v1/callbot/usage/monthly — 그 달 사용량이 있는 모든 회사(사용량만). 페이지네이션 없음.
   * @param {string} month 'YYYY-MM'
   * @returns {Promise<UsageMonthlyResponse>}
   */
  listMonthlyUsage(month) {
    return callApi.get('/api/v1/callbot/usage/monthly', { params: { month } });
  },

  /**
   * GET .../billing/settlement — 헤더와 정산·요율·대사 탭이 같이 쓰는 응답 하나.
   * @param {string} companyId
   * @param {string} month
   * @returns {Promise<SettlementDetail>}
   */
  getSettlement(companyId, month) {
    return callApi.get(`${companyBillingBase(companyId)}/settlement`, { params: { month } });
  },

  // -------------------------------------------------------------------------
  // 3-1. 기본 요율 (회사 횡단)
  // -------------------------------------------------------------------------

  /**
   * GET /api/v1/callbot/billing/default-plans — 버전 목록(적용 시작 월 내림차순)과 원가 참고값.
   * `fee_start_warning`(call-api 1-3, 계약 묶음 9.5 — G-FEE-START): 상담사 번호 요금 블록이 있는 가장 이른 버전의 달
   * (`first_fee_month`, monthly 0 블록도 센다)이 수납 시작 월(`from_month` — 결제 채널 fromMonth, 없으면 null)보다 이르거나 수납
   * 시작 월이 없으면 채우고, 아니면 null 이다. 키는 늘 있다(옛 call-api 에는 없다 — 화면은 숨긴다). GET 에만 있어 저장·삭제
   * 뒤에는 목록을 다시 부른다.
   * @returns {Promise<{ items: DefaultPlan[], cost_rates: CostRates, fee_start_warning?: { first_fee_month: string, from_month: string|null, message: string }|null }>}
   */
  listDefaultPlans() {
    return callApi.get(`${BILLING_BASE}/default-plans`);
  },

  /**
   * PUT .../default-plans/:effectiveFrom — 그 버전을 만들거나 통째로 바꾼다(판매가 칸 필요 — 회선 월 금액만 null = 미정).
   * 예전 본문은 400 이다(RatePlanBody).
   * @param {string} effectiveFrom 'YYYY-MM'
   * @param {RatePlanBody} body service/settlementRules.js formValuesToDefaultPlanBody
   * @returns {Promise<DefaultPlan>}
   */
  putDefaultPlan(effectiveFrom, body) {
    return callApi.put(`${BILLING_BASE}/default-plans/${encodeURIComponent(effectiveFrom)}`, body);
  },

  /**
   * DELETE .../default-plans/:effectiveFrom — 409 PLAN_IN_USE(확정 정산이 씀)·DEFAULT_PLAN_REQUIRED(마지막 버전),
   * 404 PLAN_NOT_FOUND.
   * @param {string} effectiveFrom
   * @returns {Promise<unknown>}
   */
  deleteDefaultPlan(effectiveFrom) {
    return callApi.delete(`${BILLING_BASE}/default-plans/${encodeURIComponent(effectiveFrom)}`);
  },

  // -------------------------------------------------------------------------
  // 3-2. 회사 예외
  // -------------------------------------------------------------------------

  /**
   * GET .../billing/plan-overrides — 예외 버전 목록(적용 시작 월 내림차순)과 원가 참고값.
   * @param {string} companyId
   * @returns {Promise<{ items: PlanOverride[], cost_rates: CostRates }>}
   */
  listPlanOverrides(companyId) {
    return callApi.get(`${companyBillingBase(companyId)}/plan-overrides`);
  },

  /**
   * PUT .../plan-overrides/:effectiveFrom — 덮을 칸만 담는다(요금표 v70 칸 — PlanOverride). memo 필수. null 은 400,
   * 예전 칸(`ai.price_per_unit`·`counselor.*.price`)은 unknown_key 400.
   * @param {string} companyId
   * @param {string} effectiveFrom
   * @param {Object} body service/settlementRules.js formValuesToOverrideBody
   * @returns {Promise<PlanOverride>}
   */
  putPlanOverride(companyId, effectiveFrom, body) {
    return callApi.put(
      `${companyBillingBase(companyId)}/plan-overrides/${encodeURIComponent(effectiveFrom)}`,
      body
    );
  },

  /**
   * DELETE .../plan-overrides/:effectiveFrom — 409 PLAN_IN_USE, 404 PLAN_OVERRIDE_NOT_FOUND.
   * @param {string} companyId
   * @param {string} effectiveFrom
   * @returns {Promise<unknown>}
   */
  deletePlanOverride(companyId, effectiveFrom) {
    return callApi.delete(
      `${companyBillingBase(companyId)}/plan-overrides/${encodeURIComponent(effectiveFrom)}`
    );
  },

  // -------------------------------------------------------------------------
  // 4-1. 자동 마감 현황·수동 실행 (회사 횡단)
  // -------------------------------------------------------------------------

  /**
   * GET /api/v1/callbot/billing/close-runs — 그 달 자동 마감 요약(DB 만 읽는다).
   * @param {string} month
   * @returns {Promise<CloseRunsResponse>}
   */
  getCloseRuns(month) {
    return callApi.get(`${BILLING_BASE}/close-runs`, { params: { month } });
  },

  /**
   * POST /api/v1/callbot/billing/close-runs — 재실행 도구. 끝난 달만(아니면 409 MONTH_NOT_ENDED). 자동 마감 스위치와 상관없이
   * 돈다. 정산 인덱스가 준비되지 않았으면 503 BILLING_INDEXES_NOT_READY(회신 표 11).
   * 비동기라 202 `{ run_id }` 만 오고, 결과는 마감 현황·상세 상태로 본다.
   * @param {{ month: string, companyId?: string|null }} args companyId 가 있으면 그 병원만
   * @returns {Promise<{ run_id: string }>}
   */
  startCloseRun({ month, companyId } = {}) {
    const body = { month };
    if (companyId) body.company_id = companyId;
    return callApi.post(`${BILLING_BASE}/close-runs`, body);
  },

  // -------------------------------------------------------------------------
  // 4-2. 사람이 하는 일 — 저장·다시 계산·확정·재수정 (쓰기마다 rev +1)
  // 자동 마감 잡의 쓰기도 rev 를 올린다 — 경합이 겹치면 409 STATEMENT_CONFLICT(다시 불러와 다시 시도, 회신 표 10).
  // 정산서가 없으면 넷 다 404 STATEMENT_NOT_FOUND.
  // -------------------------------------------------------------------------

  /**
   * PUT .../statements/:month — 검토 필요 정산의 줄 금액·메모 저장(자동 마감이 다시 계산하지 않게 된다).
   * review 가 아니면 409 STATEMENT_NOT_IN_REVIEW. v70 이전 정산서면 409 STATEMENT_LEGACY_LINES(개정 8).
   * @param {string} companyId
   * @param {string} month
   * @param {{ lines: Partial<Record<'base'|'inbound'|'outbound'|'web_text'|'line'|'counselor_number', { amount: number|null, memo?: string }>>, memo?: string }} body
   *   amount: 0 ~ 10,000,000,000 정수(넘으면 400), null 이면 계산값을 따른다(overridden 이 풀린다). 사람이 넣은 금액은 다시
   *   계산해도 남는다. 예전 줄 키 `ai`·`counselor` 는 400(개정 6) — 상담사 번호 추가는 `counselor_number`(call-api 1-2. 1-2 전
   *   call-api 는 이 키를 조용히 버린다). 응답의 `warnings` 는 상담사 번호 요금 중복 의심을 다시 판정한 값이다
   * @returns {Promise<Statement>}
   */
  saveStatement(companyId, month, body) {
    return callApi.put(statementBase(companyId, month), body);
  },

  /**
   * POST .../statements/:month/recalculate — 사람이 넣은 줄은 두고 나머지 줄·경고·스냅샷을 다시 계산(review 에서만).
   * @param {string} companyId
   * @param {string} month
   * @returns {Promise<Statement>}
   */
  recalculateStatement(companyId, month) {
    return callApi.post(`${statementBase(companyId, month)}/recalculate`, {});
  },

  /**
   * POST .../statements/:month/confirm — 남은 차단 경고를 모두 확인해야 한다
   * (409 STATEMENT_HAS_WARNINGS `details.warnings`, 409 STATEMENT_NOT_IN_REVIEW, 404 STATEMENT_NOT_FOUND,
   * v70 이전 정산서면 STATEMENT_HAS_WARNINGS 보다 먼저 409 STATEMENT_LEGACY_LINES).
   * 확정 고정(계약 묶음 9.2): expected_rev 가 있으면 늘 비교해 다르면 409 STATEMENT_CHANGED `details { expected_rev, current_rev }`,
   * expected_total 이 있고 정산서 total 과 다르면 같은 코드에 `details { expected_total, current_total }`. 결제가 걸린 달(payment_scope
   * in, 판단 읽기 실패 null 포함)에 expected_rev 가 없으면 409 PAYMENT_EXPECTED_REV_REQUIRED. 옛 call-api 는 두 칸을 조용히 버린다.
   * @param {string} companyId
   * @param {string} month
   * @param {{ acknowledged_warnings: string[], expected_rev?: number, expected_total?: number }} body
   *   expected_rev 는 화면이 확정하려는 정산서의 rev(먼저 저장했으면 저장 응답의 rev)로, 결제가 걸리지 않은 달에도 보낸다(AC 결제
   *   요청 결론 1-1). expected_total 은 확인창 공급가액이 견줄 상세(입력이 없으면 지금 상세, 두 번 묻는 흐름이면 다시 불러온 상세)의
   *   statement.total 과 같을 때만 보내고, 한 번 묻는 흐름에서 저장과 확정을 한 번에 할 때는 보내지 않는다(AC 결제 요청 계약 1) —
   *   service/settlementPayment.js confirmBody·expectedTotalFor 가 만든다
   * @returns {Promise<Statement>}
   */
  confirmStatement(companyId, month, body) {
    return callApi.post(`${statementBase(companyId, month)}/confirm`, body);
  },

  /**
   * POST .../statements/:month/reopen — 확정을 검토 필요로 되돌린다(재수정). memo 필수.
   * confirmed 가 아니면 409 STATEMENT_NOT_CONFIRMED.
   * 그 병원·달의 결제 요청 문서가 있으면 call-api 가 먼저 정지 표식을 쓴다 — 쓰지 못하면(요청 문서를 읽지 못함 포함) 503
   * PAYMENT_STOP_FAILED 로 재수정하지 않는다(계약 묶음 9.3. 정산서는 그대로다).
   * @param {string} companyId
   * @param {string} month
   * @param {{ memo: string }} body
   * @returns {Promise<Statement>}
   */
  reopenStatement(companyId, month, body) {
    return callApi.post(`${statementBase(companyId, month)}/reopen`, body);
  },

  // -------------------------------------------------------------------------
  // 4-4. 일회성 항목 — confirmed·closing(lease 유효) 달은 쓰기 409 STATEMENT_LOCKED, 경합은 409 STATEMENT_CONFLICT,
  // 그 달 정산서가 v70 이전이면 409 STATEMENT_LEGACY_LINES(항목을 쓰기 전에 막는다 — 개정 8)
  // -------------------------------------------------------------------------

  /**
   * GET .../billing/one-off-items?month=
   * @param {string} companyId
   * @param {string} month
   * @returns {Promise<{ items: OneOffItem[], amount: number }>}
   */
  listOneOffItems(companyId, month) {
    return callApi.get(`${companyBillingBase(companyId)}/one-off-items`, { params: { month } });
  },

  /**
   * POST .../billing/one-off-items
   * @param {string} companyId
   * @param {{ month: string, name: string, amount: number, memo?: string }} body
   * @returns {Promise<OneOffItem>}
   */
  createOneOffItem(companyId, body) {
    return callApi.post(`${companyBillingBase(companyId)}/one-off-items`, body);
  },

  /**
   * PUT .../billing/one-off-items/:itemId — 이름·금액·메모(달은 바꾸지 않는다). 없으면 404 ONE_OFF_ITEM_NOT_FOUND.
   * @param {string} companyId
   * @param {string} itemId
   * @param {{ name: string, amount: number, memo?: string }} body
   * @returns {Promise<OneOffItem>}
   */
  updateOneOffItem(companyId, itemId, body) {
    return callApi.put(
      `${companyBillingBase(companyId)}/one-off-items/${encodeURIComponent(itemId)}`,
      body
    );
  },

  /**
   * DELETE .../billing/one-off-items/:itemId
   * @param {string} companyId
   * @param {string} itemId
   * @returns {Promise<unknown>}
   */
  deleteOneOffItem(companyId, itemId) {
    return callApi.delete(
      `${companyBillingBase(companyId)}/one-off-items/${encodeURIComponent(itemId)}`
    );
  },

  // -------------------------------------------------------------------------
  // 5. 건수 대사 — 화면에서 vox 를 부르는 유일한 라우트. 버튼을 눌렀을 때만 부른다.
  // -------------------------------------------------------------------------

  /**
   * GET .../billing/reconciliation?month=&missing_ids=true|false
   * @param {string} companyId
   * @param {string} month
   * @param {{ missingIds?: boolean }} [opts] true 면 누락 통화 ID 차집합까지(느리다, 속도 제한)
   * @returns {Promise<ReconciliationResponse>}
   */
  getReconciliation(companyId, month, { missingIds = false } = {}) {
    return callApi.get(`${companyBillingBase(companyId)}/reconciliation`, {
      params: { month, missing_ids: missingIds ? 'true' : 'false' },
    });
  },

  // -------------------------------------------------------------------------
  // 6. 재사용 — 병원 월간 이용 리포트·통화 단건 (연결 진단 탭)
  // -------------------------------------------------------------------------

  /**
   * GET .../callbot/stats/monthly?month= — 병원이 studio 에서 보는 월간 이용 리포트(원가·금액 필드 없음).
   * 형태: ~/md/front/콜봇-월간이용리포트-프론트요청.md "요청 계약"
   * 연결 시도 필드(~/md/front/콜봇-연결시도-attempts-call-api구현회신.md):
   * `per_number[].attempts`·`per_number_unattributed.attempts` = { total, answered, no_answer_30s, unavailable,
   * caller_hangup_ringing, no_ring, superseded, other }|null, `per_number[].attempts_collected_from` = 'YYYY-MM-DD'(KST)|null,
   * `flow.handoffs_attempts_expected`·`flow.handoffs_with_attempts` = number|null(수집률 분모·분자). null = 모름, 0 = 없음
   * @param {string} companyId
   * @param {string} month
   * @returns {Promise<Object>}
   */
  getMonthlyStats(companyId, month) {
    return callApi.get(`${companyCallbotBase(companyId)}/stats/monthly`, { params: { month } });
  },

  /**
   * GET .../callbot/pbx-calls/:voxCallId — 통화 1건의 상담사 연결 leg 와 연결 시도(`attempts`). 404 = 콜봇 단독 통화.
   * @param {string} companyId
   * @param {string} voxCallId
   * @returns {Promise<PbxCall>}
   */
  getPbxCall(companyId, voxCallId) {
    return callApi.get(`${companyCallbotBase(companyId)}/pbx-calls/${encodeURIComponent(voxCallId)}`);
  },

  // -------------------------------------------------------------------------
  // 7. 정산 시작일 — 회사 경로(BillingSettlementController). 지금 값·이력 / 바꾸기 전 영향 / 저장
  // -------------------------------------------------------------------------

  /**
   * GET .../billing/settlement-start
   * @param {string} companyId
   * @returns {Promise<SettlementStartDoc>}
   */
  getSettlementStart(companyId) {
    return callApi.get(`${companyBillingBase(companyId)}/settlement-start`);
  },

  /**
   * GET .../billing/settlement-start/preview?start_date= — 쓰기 없는 영향 계산. startDate 가 없으면 지우기(null)의 영향.
   * 시작일이 그대로여도 시작 전 달에 남은 정산서가 있으면 그 달을 discard_required 로 돌려준다(남은 정산서 정리).
   * @param {string} companyId
   * @param {string|null} startDate 'YYYY-MM-DD' | null
   * @returns {Promise<SettlementStartPreview>}
   */
  previewSettlementStart(companyId, startDate) {
    return callApi.get(`${companyBillingBase(companyId)}/settlement-start/preview`, {
      params: startDate ? { start_date: startDate } : {},
    });
  },

  /**
   * PUT .../billing/settlement-start — 본문은 다섯 키 그대로(service/settlementStart.js buildSettlementStartBody). 응답은 GET 모양 +
   * affected_months(PUT action). 이용 내역 필수 이벤트라 기록에 실패하면 503 AUDIT_UNAVAILABLE 이고 시작일은 그대로다.
   * @param {string} companyId
   * @param {SettlementStartBody} body
   * @returns {Promise<SettlementStartDoc & { affected_months: SettlementStartAffectedMonth[] }>}
   */
  putSettlementStart(companyId, body) {
    return callApi.put(`${companyBillingBase(companyId)}/settlement-start`, body);
  },

  // -------------------------------------------------------------------------
  // 8. 정산 엑셀 내보내기 — 회사 횡단(UsageListController). 이용 내역에 남는다(서버 recordNow)
  // -------------------------------------------------------------------------

  /**
   * POST /api/v1/callbot/usage/monthly/export — 이 호출만 timeout 을 늘린다(SETTLEMENT_EXPORT_TIMEOUT_MS). 응답은 no-store 다 —
   * 화면은 받은 JSON 으로 파일을 만들고 응답을 남겨 두지 않는다.
   * @param {SettlementExportBody} body service/settlementExportSelection.js buildSettlementExportBody
   * @param {{ timeout?: number }} [opts]
   * @returns {Promise<SettlementExportResponse>}
   */
  exportMonthlySettlement(body, { timeout = SETTLEMENT_EXPORT_TIMEOUT_MS } = {}) {
    return callApi.post('/api/v1/callbot/usage/monthly/export', body, { timeout });
  },
};
